import { useState, useCallback, useEffect, useMemo } from 'react';
import { getVersionTitleList } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getVersionConfig,
  type IVersionConfigLevel,
} from '../VersionAnalysis/config';

export interface IVersionTitleItem {
  update_title_en: string;
  update_title_cn: string;
  start_time: string;
  end_time: string;
}

export interface IVersionCascadeOption {
  value: string;
  label: string;
  children?: IVersionCascadeOption[];
}

interface UseVersionTitleListOptions {
  gameInfo: any;
  filter?: any;
}

/** 根据 versionConfig 递归解析版本树为级联选项
 *  versionFilters = true 时，叶子 value 拼接父级值保证唯一性（如 "3__4"）
 *  versionFilters = false 时，value 直接使用原始值
 */
function parseVersionTree(
  versionTree: any[],
  config: IVersionConfigLevel[],
  versionFilters = false,
  parentValue = '',
): IVersionCascadeOption[] {
  if (!config.length || !versionTree) return [];

  const [currentLevel, ...remainingLevels] = config;
  const levelKey = currentLevel.value;
  const childrenKey = remainingLevels.length
    ? `${remainingLevels[0].value}s`
    : '';

  return versionTree.map(node => {
    const rawValue = node[levelKey];
    const value =
      versionFilters && parentValue
        ? `${parentValue}__${rawValue}`
        : rawValue;
    return {
      value,
      label: rawValue,
      children: childrenKey
        ? parseVersionTree(
          node[childrenKey] || [],
          remainingLevels,
          versionFilters,
          value,
        )
        : undefined,
    };
  });
}

export const useVersionTitleList = ({
  gameInfo,
  filter,
}: UseVersionTitleListOptions) => {
  const versionConfig = useMemo(
    () => getVersionConfig(gameInfo?.edition_unified_id),
    [gameInfo?.edition_unified_id],
  );

  const isConfigGame = useMemo(() => !!versionConfig, [versionConfig]);

  const versionLevels = versionConfig?.levels ?? null;
  const treeKey = versionConfig?.treeKey ?? 'version_tree';

  const [titleList, setTitleList] = useState<IVersionTitleItem[]>([]);
  const [cascadeOptions, setCascadeOptions] = useState<IVersionCascadeOption[] | null>(null);

  const { wrapperCancelRequest, loading } = useCancelRequestHook<any>();

  const fetchTitleList = useCallback(async () => {
    if (!gameInfo?.edition_unified_id) return;

    try {
      const res = await wrapperCancelRequest(getVersionTitleList, {
        ...gameInfo,
        date_type: filter?.date_type || 'daily',
        start_time: filter?.start_time || '',
        end_time: filter?.end_time || '',
        is_dune: isConfigGame,
        channel: ['steam'],
        channel_type: 'comments',
        steam_filter_type: 'steam',
      });

      setTitleList(res?.list || []);

      if (isConfigGame && versionLevels) {
        const treeData = res?.[treeKey];
        if (treeData) {
          setCascadeOptions(
            parseVersionTree(
              treeData,
              versionLevels,
              !!versionConfig?.version_filters,
            ),
          );
        } else {
          setCascadeOptions(null);
        }
      } else {
        setCascadeOptions(null);
      }
    } catch (err) {
      console.log(err);
    }
  }, [filter?.date_type, filter?.start_time, filter?.end_time, isConfigGame]);

  useEffect(() => {
    const { start_time, end_time } = filter || {};
    if (!gameInfo?.edition_unified_id || !start_time || !end_time) return;
    fetchTitleList();
  }, [fetchTitleList]);

  return {
    titleList,
    cascadeOptions,
    isConfigGame,
    versionConfig,
    loading,
    refresh: fetchTitleList,
  };
};
